// src/pages/SmartPages/TargetPage.tsx
import React, { useMemo } from 'react';
import { Navigate, useParams } from '@/utils/router';

import UniversalPage from '@/pages/UniversalPage';
import { buildRuntimePageConfig } from '@/pages/runtime-page-config';

import {
  type EventType,
  type Region,
  type SeoCity,
  slugToTypeName,
  slugToRegionName,
  resolveCityFromSlugMaybe,
  findRegionByCity,
  globalTypePath,
  isRegionName,
} from '@/constants/geo';

import { useSiteReviews } from '@/context/reviews';
import { ReviewItem } from '@/constants/core';
import { useI18n } from '@/i18n/LanguageProvider';

const safeDecode = (s?: string) => {
  if (!s) return '';
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
};

type ResolvedLocation =
  | { kind: 'global' }
  | { kind: 'region'; region: Region }
  | { kind: 'city'; city: SeoCity; region: Region }
  | { kind: 'unknown' };

  const TargetPage: React.FC = () => {
    const { type: typeSlug = '', location } =
      useParams<{ type?: string; location?: string }>();
  
    const { reviews } = useSiteReviews();
    const { lang, i18n } = useI18n(); // ⭐️ NEW
  
    const siteReviews = Array.isArray(reviews)
      ? (reviews as ReviewItem[])
      : undefined;
  
    // ---------- type ----------
    const type: EventType | null = useMemo(
      () => slugToTypeName(typeSlug),
      [typeSlug]
    );
  
    const isGlobal = !location;
  
    // ---------- location resolve ----------
    const resolved: ResolvedLocation = useMemo(() => {
      if (!type) return { kind: 'unknown' };
      if (isGlobal) return { kind: 'global' };
  
      const locDecoded = safeDecode(location || '');
  
      const maybeCity = resolveCityFromSlugMaybe(locDecoded);
      if (maybeCity) {
        const region =
          (findRegionByCity(String(maybeCity)) ?? 'מרכז') as Region;
        return { kind: 'city', city: maybeCity as SeoCity, region };
      }
  
      const maybeRegion = slugToRegionName(locDecoded);
      if (isRegionName(maybeRegion)) {
        return { kind: 'region', region: maybeRegion as Region };
      }
  
      return { kind: 'unknown' };
    }, [type, isGlobal, location]);
  
    // ---------- page config ----------
    const cfg = useMemo(() => {
      if (!type) return null;
      const configLang = i18n.HTML_LANG as typeof lang;
  
      if (resolved.kind === 'global') {
        return buildRuntimePageConfig('target-global', {
          type,
          siteReviews,
          lang: configLang, // ⭐️ IMPORTANT
          i18n,
        });
      }
  
      if (resolved.kind === 'region') {
        return buildRuntimePageConfig('target-region', {
          type,
          region: resolved.region,
          siteReviews,
          lang: configLang,
          i18n,
        });
      }
  
      if (resolved.kind === 'city') {
        return buildRuntimePageConfig('target-city', {
          type,
          region: resolved.region,
          city: resolved.city,
          siteReviews,
          lang: configLang,
          i18n,
        });
      }
  
      return null;
    }, [type, resolved, siteReviews, i18n]);
  
    // ---------- redirects ----------
    if (!type) return <Navigate to="/" replace />;
  
    if (resolved.kind === 'unknown') {
      return <Navigate to={globalTypePath(type)} replace />;
    }
  
    if (!cfg) {
      return <Navigate to={globalTypePath(type)} replace />;
    }
  
    // ---------- render ----------
    return (
      <UniversalPage
        overrides={cfg.overrides}
        internalLinks={cfg.internalLinks}
        serviceForSection5={cfg.serviceForSection5}
        eventType={type}
        showFooterPlanningGuideLink={false}
        region={
          resolved.kind === 'region' || resolved.kind === 'city'
            ? resolved.region
            : 'מרכז'
        }
      />
    );
  };

export default TargetPage;
